import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Box, Divider, makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { useSortingCenterContractsForUserId } from 'hooks';
import Loading from 'components/Loading';
import { colors, fonts } from 'theme';
import { AppContext } from 'context';
import { checkDateIsStrictlyLower, formatDateForDisplay } from 'utils/date';
import { SortingCenterContract, SortingCenterContractStatus, UserRole } from 'types';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { useLatestClosure } from 'utils/closureHooks';
import CreateButton from 'components/Buttons/CreateButton';
import { Routes } from 'routes';
import ActionButton from 'components/Buttons/ActionButton';
import EditBarIcon from 'components/Icons/EditBarIcon';

const useStyles = makeStyles({
  tablePaper: {
    flex: 1,
    display: 'flex',
    margin: '2em 0 36px 0',
    padding: '0 21px 2em 21px',
  },
  table: {
    marginTop: '15px',
    borderCollapse: 'separate',
    borderSpacing: '0px 10px',
  },
  headerRow: {
    '& th': {
      fontFamily: fonts.secondary,
      fontWeight: 600,
      padding: '15px 0',
      border: 'none',
      textAlign: 'center',
      '&:first-child': {
        textAlign: 'left',
        paddingLeft: '44px',
      },
      '&:last-child': {
        textAlign: 'right',
        paddingRight: '34px',
      },
    },
  },
  rowStyle: {
    backgroundColor: colors.white,
    '& td': {
      padding: '15px 0',
      borderBottom: 'none',
      fontFamily: fonts.secondary,
      textAlign: 'center',
      '&:first-child': {
        paddingLeft: '34px',
        textAlign: 'left',
      },
      '& td:last-child': {
        textAlign: 'right',
        paddingRight: '34px',
      },
    },
  },
  rowClosed: {
    '& td': {
      '&:first-child': {
        borderLeft: `10px solid ${colors.red}`,
      },
      '&:nth-child(5)': {
        color: colors.red,
      },
      '&:last-child': {
        paddingRight: '34px',
        textAlign: 'right',
      },
    },
  },
  rowUpcoming: {
    '& td': {
      '&:first-child': {
        borderLeft: `10px solid ${colors.darkOrange}`,
      },
      '&:nth-child(5)': {
        color: colors.darkOrange,
      },
      '&:last-child': {
        paddingRight: '34px',
        textAlign: 'right',
      },
    },
  },
  rowEndOfProcessing: {
    '& td': {
      '&:first-child': {
        borderLeft: `10px solid ${colors.darkOrange}`,
      },
      '&:nth-child(5)': {
        color: colors.darkOrange,
      },
      '&:last-child': {
        paddingRight: '34px',
        textAlign: 'right',
      },
    },
  },
  rowOngoing: {
    '& td': {
      '&:first-child': {
        borderLeft: `10px solid ${colors.primary}`,
      },
      '&:nth-child(5)': {
        color: colors.primary,
      },
      '&:last-child': {
        paddingRight: '34px',
        textAlign: 'right',
      },
    },
  },
  tableCellActions: {
    display: 'inline-flex',
    paddingRight: '0px !important',
  },
  contractDate: {
    marginLeft: '0.5em',
  },
});

const getClientContractStatus = (contract: SortingCenterContract, latestClosureDate: Date | null) => {
  if (!latestClosureDate || !latestClosureDate) {
    return null;
  }

  if (contract.isBalanced) {
    return SortingCenterContractStatus.CLOSED;
  }
  const today = new Date();
  const contractStartDate = new Date(contract.startDate);
  const contractEndDate = new Date(contract.endDate);

  if (checkDateIsStrictlyLower(today, contractStartDate)) {
    return SortingCenterContractStatus.UPCOMING;
  } else if (
    contractEndDate.getMonth() <= latestClosureDate.getMonth() + 1 &&
    contractEndDate.getFullYear() <= latestClosureDate.getFullYear()
  ) {
    return SortingCenterContractStatus.END_OF_PROCESSING;
  }
  return SortingCenterContractStatus.ONGOING;
};

const SortingCenterContractList: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'sortingCenterContracts' });

  const { clientId } = useParams<{ clientId: string }>();
  const [latestClosure] = useLatestClosure();

  const context = useContext(AppContext);
  const roles = context.getRoles?.();
  const sortingCenter = context.getSortingCenter?.();
  const { value: contractList } = useSortingCenterContractsForUserId(
    transformObjectId(sortingCenter),
    Number(clientId),
  );
  const isUserAbleToAddContract =
    sortingCenter?.isActive && roles && (roles.includes(UserRole.ROLE_MANAGER) || context.hasLeastOneAdminRole());
  const isUserAbleToEditContract =
    sortingCenter?.isActive && roles && (roles.includes(UserRole.ROLE_MANAGER) || context.hasLeastOneAdminRole());
  return (
    <Box className={classes.tablePaper}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow className={classes.headerRow}>
            <TableCell width="20%">{t('stream')}</TableCell>
            <TableCell width="16%">{t('startDate')}</TableCell>
            <TableCell width="16%">{t('endDate')}</TableCell>
            <TableCell width="20%">{t('characterizationNumber')}</TableCell>
            <TableCell width="20%">{t('status')}</TableCell>
            {isUserAbleToEditContract && <TableCell width="4%" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {contractList !== null && contractList !== undefined ? (
            <>
              {contractList.map((contract, index) => {
                const latestClosureDate = latestClosure
                  ? new Date(latestClosure.year, latestClosure.month, 1)
                  : sortingCenter
                  ? new Date(sortingCenter?.initializationDate)
                  : null;
                const contractStatus = getClientContractStatus(contract, latestClosureDate);
                const contractId = contract.id;
                const streamId = contract.stream.id;
                return (
                  <TableRow
                    key={`sorting_center_contract_${index}`}
                    className={classNames(classes.rowStyle, {
                      [classes.rowClosed]: contractStatus === SortingCenterContractStatus.CLOSED,
                      [classes.rowUpcoming]: contractStatus === SortingCenterContractStatus.UPCOMING,
                      [classes.rowOngoing]: contractStatus === SortingCenterContractStatus.ONGOING,
                      [classes.rowEndOfProcessing]: contractStatus === SortingCenterContractStatus.END_OF_PROCESSING,
                    })}
                  >
                    <TableCell>{contract.stream.name}</TableCell>
                    <TableCell>{formatDateForDisplay(contract.startDate)}</TableCell>
                    <TableCell>{formatDateForDisplay(contract.endDate)}</TableCell>
                    <TableCell>{contract.characterizationNumber}</TableCell>
                    <TableCell>
                      {t(`contractStatus.${contractStatus}`)}
                      {contract.dateFermetureContrat && (
                        <span className={classes.contractDate}>
                          (
                          {new Date(contract.dateFermetureContrat).toLocaleDateString('fr-FR', {
                            month: '2-digit',
                            year: 'numeric',
                          })}
                          )
                        </span>
                      )}
                    </TableCell>
                    {isUserAbleToEditContract && (
                      <TableCell className={classes.tableCellActions}>
                        <Divider orientation={'vertical'} flexItem />
                        <ActionButton
                          icon={EditBarIcon}
                          style={contract.isBalanced ? { color: colors.grey100 } : { color: colors.grey900 }}
                          component={Link}
                          data-qa-id={'edit-button'}
                          disabled={contract.isBalanced}
                          to={generatePath(Routes.SortingCenterContractUpdate, { clientId, contractId, streamId })}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {isUserAbleToAddContract && (
                <CreateButton
                  component={Link}
                  to={generatePath(Routes.SortingCenterContractCreate, { clientId })}
                  icon={null}
                  label={t('create.buttonLinkText')}
                />
              )}
            </>
          ) : (
            <Loading />
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
export default SortingCenterContractList;
